import React from 'react';
import { useNavigate } from 'react-router-dom';

import { TaskDetail } from '@/components/tasks/task-detail';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/catalyst/badge';
import {
  Dialog,
  DialogTitle,
  RightDialogContent,
} from '@/components/ui/dialog';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { Task, TaskSearchResults } from '@/services/types/client-intake-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
  CanonicalTaskType,
  TaskStatusColors,
} from '@/services/types/task-types';

import { LatestTaskUpdate } from '../medical-records-dashboard/LatestTaskUpdate';

interface MatterGroup {
  matterId: string;
  matterName: string;
  tasks: Task[];
}

interface FirmGroup {
  firmId: string;
  firmName: string;
  matters: MatterGroup[];
}

interface DepartmentGroup {
  departmentId: string;
  departmentName: string;
  stateCode: string | null;
  tasks: Task[];
}

const groupTasksByFirmMatter = (tasks: Task[]): FirmGroup[] => {
  const byFirm = tasks.reduce((acc, task) => {
    const firmId = task.firm;
    const firmName = task.firm_name;
    const matterId = task.matter;
    const matterName = task.matter_name;

    if (!acc.has(firmId)) {
      acc.set(firmId, {
        name: firmName,
        matters: new Map<string, { name: string; tasks: Task[] }>(),
      });
    }
    const firmEntry = acc.get(firmId)!;

    if (!firmEntry.matters.has(matterId)) {
      firmEntry.matters.set(matterId, { name: matterName, tasks: [] });
    }
    const matterEntry = firmEntry.matters.get(matterId)!;
    matterEntry.tasks.push(task);

    return acc;
  }, new Map<string, { name: string; matters: Map<string, { name: string; tasks: Task[] }> }>());

  return Array.from(byFirm.entries())
    .map(([firmId, firmVal]) => {
      const matters: MatterGroup[] = Array.from(firmVal.matters.entries())
        .map(([matterId, matterVal]) => ({
          matterId,
          matterName: matterVal.name,
          tasks: matterVal.tasks,
        }))
        .sort((a, b) => a.matterName.localeCompare(b.matterName));
      return { firmId, firmName: firmVal.name, matters };
    })
    .sort((a, b) => a.firmName.localeCompare(b.firmName));
};

const groupTasksByDepartment = (tasks: Task[]): DepartmentGroup[] => {
  const byDept = tasks.reduce((acc, task) => {
    const deptId = task.police_report?.police_department?.id || 'unknown-dept';
    const deptName =
      task.police_report?.police_department?.name || 'Unknown Department';
    const stateCode = task.police_report?.police_department?.state_code || null;

    if (!acc.has(deptId)) {
      acc.set(deptId, {
        name: deptName,
        stateCode,
        tasks: [] as Task[],
      });
    }
    const entry = acc.get(deptId)!;
    entry.tasks.push(task);
    return acc;
  }, new Map<string, { name: string; stateCode: string | null; tasks: Task[] }>());

  return Array.from(byDept.entries())
    .map(([departmentId, val]) => ({
      departmentId,
      departmentName: val.name,
      stateCode: val.stateCode,
      tasks: val.tasks,
    }))
    .sort((a, b) => a.departmentName.localeCompare(b.departmentName));
};

const StatusBadge: React.FC<{ status: CanonicalTaskStatus }> = ({ status }) => {
  const color = TaskStatusColors[status] || 'bg-gray-100 text-gray-700';
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${color}`}
    >
      {status}
    </span>
  );
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
};

export const PoliceReportsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = React.useState<Task | null>(null);
  const [viewMode, setViewMode] = React.useState<'matter' | 'department'>(
    'matter',
  );

  // Fetch police report group tasks via canonical group
  const { data, isLoading, isError } = useGetTasksQuery({
    canonical_groups: [CanonicalTaskGroup.POLICE_REPORT_INVESTIGATION],
    limit: 500,
    offset: 0,
    ordering: '-order,-modified_at',
  });

  const allTasks: Task[] =
    (data as TaskSearchResults | undefined)?.results ?? [];

  const policeTypes: CanonicalTaskType[] = [
    CanonicalTaskType.POLICE_REPORT,
    CanonicalTaskType.LOCATE_REPORT_SOURCE,
  ];

  const outstandingStatuses: CanonicalTaskStatus[] = [
    CanonicalTaskStatus.PENDING,
    CanonicalTaskStatus.IN_PROGRESS,
    CanonicalTaskStatus.BLOCKED,
  ];

  const outstandingPoliceTasks = allTasks.filter(
    (t) =>
      t.status?.canonical_status &&
      outstandingStatuses.includes(t.status.canonical_status) &&
      policeTypes.includes(t.type?.canonical_type as CanonicalTaskType),
  );

  const groupedByMatter = groupTasksByFirmMatter(outstandingPoliceTasks);
  const groupedByDepartment = groupTasksByDepartment(outstandingPoliceTasks);

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-gray-600">
        Loading police report tasks…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-6 text-sm text-red-600">
        Failed to load police report tasks.
      </div>
    );
  }

  const header = (
    <div className="flex items-center justify-between">
      <h1 className="text-xl font-semibold text-gray-900">
        Police Reports Dashboard
      </h1>
      <div className="flex items-center gap-2">
        <div className="flex rounded-md overflow-hidden border border-gray-200">
          <Button
            type="button"
            variant={viewMode === 'matter' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('matter')}
            aria-label="Group by Matter"
          >
            By Matter
          </Button>
          <Button
            type="button"
            variant={viewMode === 'department' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('department')}
            aria-label="Group by Department"
          >
            By Department
          </Button>
        </div>
        <Badge color="blue">{outstandingPoliceTasks.length} Outstanding</Badge>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {header}

      {viewMode === 'matter' ? (
        groupedByMatter.length === 0 ? (
          <div className="text-sm text-gray-600">
            No outstanding police report tasks.
          </div>
        ) : (
          <div className="space-y-6">
            {groupedByMatter.map((firm) => (
              <div
                key={firm.firmId}
                className="border border-gray-200 rounded-lg"
              >
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">
                      {firm.firmName}
                    </span>
                    <span className="text-xs text-gray-500">
                      {firm.matters.length} matters
                    </span>
                  </div>
                  <Badge>
                    {firm.matters.reduce((acc, m) => acc + m.tasks.length, 0)}{' '}
                    tasks
                  </Badge>
                </div>

                {firm.matters.map((matter) => (
                  <div
                    key={matter.matterId}
                    className="border-b last:border-b-0 border-gray-200"
                  >
                    <div className="px-4 py-3 flex items-center justify-between bg-white">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-sm font-medium text-blue-700 hover:underline"
                          onClick={() =>
                            navigate(`/matters/${matter.matterId}`)
                          }
                          aria-label={`Open matter ${matter.matterName}`}
                        >
                          {matter.matterName}
                        </button>
                        <span className="text-xs text-gray-500">
                          {matter.tasks.length} tasks
                        </span>
                      </div>
                    </div>

                    <ul className="px-2 py-2 space-y-2">
                      {matter.tasks.map((task) => (
                        <li key={task.id}>
                          <button
                            type="button"
                            className="w-full text-left"
                            onClick={() => setSelectedTask(task)}
                            aria-label={`Open task ${task.name}`}
                          >
                            <div className="rounded-md border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-colors p-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900 truncate">
                                      {task.name}
                                    </span>
                                    {task.status?.canonical_status && (
                                      <StatusBadge
                                        status={task.status.canonical_status}
                                      />
                                    )}
                                  </div>
                                  {task.description && (
                                    <p className="mt-1 text-xs text-gray-700 whitespace-pre-wrap">
                                      {task.description}
                                    </p>
                                  )}
                                  <LatestTaskUpdate
                                    matterId={task.matter}
                                    taskId={task.id}
                                  />
                                  <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-500">
                                    {task.due_date && (
                                      <span>
                                        Due {formatDate(task.due_date)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 text-[11px] text-gray-500">
                                  <span>Firm: {task.firm_name}</span>
                                  <span>
                                    Updated {formatDate(task.modified_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )
      ) : // Grouped by Department view
      groupedByDepartment.length === 0 ? (
        <div className="text-sm text-gray-600">
          No outstanding police report tasks.
        </div>
      ) : (
        <div className="space-y-6">
          {groupedByDepartment.map((dept) => (
            <div
              key={dept.departmentId}
              className="border border-gray-200 rounded-lg"
            >
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {dept.departmentName}
                  </span>
                  {dept.stateCode && (
                    <span className="text-xs text-gray-500">
                      {dept.stateCode}
                    </span>
                  )}
                </div>
                <Badge>{dept.tasks.length} tasks</Badge>
              </div>

              <ul className="px-2 py-2 space-y-2">
                {dept.tasks.map((task) => (
                  <li key={task.id}>
                    <button
                      type="button"
                      className="w-full text-left"
                      onClick={() => setSelectedTask(task)}
                      aria-label={`Open task ${task.name}`}
                    >
                      <div className="rounded-md border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-colors p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {task.name}
                              </span>
                              {task.status?.canonical_status && (
                                <StatusBadge
                                  status={task.status.canonical_status}
                                />
                              )}
                            </div>
                            {task.description && (
                              <p className="mt-1 text-xs text-gray-700 whitespace-pre-wrap">
                                {task.description}
                              </p>
                            )}
                            <LatestTaskUpdate
                              matterId={task.matter}
                              taskId={task.id}
                            />
                            <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-500">
                              <span>Matter: {task.matter_name}</span>
                              {task.due_date && (
                                <span>Due {formatDate(task.due_date)}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 text-[11px] text-gray-500">
                            <span>Firm: {task.firm_name}</span>
                            <span>Updated {formatDate(task.modified_at)}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {selectedTask && (
        <Dialog open onOpenChange={() => setSelectedTask(null)} modal={false}>
          <RightDialogContent className="bg-white h-[95vh] overflow-hidden flex flex-col">
            <DialogTitle className="sr-only">Task Details</DialogTitle>
            <TaskDetail
              taskId={selectedTask.id}
              matterId={selectedTask.matter}
              onClose={() => setSelectedTask(null)}
              isModal={false}
              task={selectedTask}
            />
          </RightDialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PoliceReportsDashboard;
